import { useState, useEffect } from "react";
import { Loader2, History, FileCog, FileCheck2, Download, FileText, Upload, Settings2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { bonificacionAuditoria, bonificacionExportarAuditoria } from "@/api";

const ACCION_LABELS = {
  regla_bonificacion_creada: { label: "Creó una nueva regla de bonificación", icon: Settings2, color: "text-municipal" },
  fichero_generado: { label: "Generó el fichero del mes", icon: FileCog, color: "text-municipal" },
  fichero_aprobado: { label: "Aprobó el fichero", icon: FileCheck2, color: "text-valoriza-green" },
  fichero_descargado: { label: "Descargó un fichero (CSV)", icon: Download, color: "text-gray-500" },
  informe_pdf_descargado: { label: "Descargó el informe PDF", icon: FileText, color: "text-gray-500" },
  fichero_retorno_subido: { label: "Subió un fichero de retorno", icon: Upload, color: "text-amber-600" },
  auditoria_exportada: { label: "Exportó el historial de auditoría a CSV", icon: Download, color: "text-gray-500" },
};

function detalleTexto(accion, detalle) {
  if (!detalle) return "";
  if (accion === "fichero_descargado") return `Periodo relacionado · tipo ${detalle.tipo}`;
  if (accion === "informe_pdf_descargado") return `Periodo ${detalle.periodo}`;
  if (accion === "fichero_generado") return `Periodo ${detalle.periodo}`;
  if (accion === "regla_bonificacion_creada") return `Versión ${detalle.version}`;
  if (accion === "fichero_retorno_subido") return `${detalle.filas_actualizadas} fila(s) actualizadas`;
  if (accion === "auditoria_exportada") return `${detalle.filas} fila(s) exportadas`;
  if (accion === "fichero_aprobado") return "";
  return "";
}

export default function AuditoriaBonificacion() {
  const [logs, setLogs] = useState(null);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);

  const load = () => {
    setLoading(true);
    bonificacionAuditoria().then(setLogs).catch(() => setLogs([])).finally(() => setLoading(false));
  };

  useEffect(() => {
    if (open && !logs) load();
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="bg-white rounded-xl shadow-sm p-6" data-testid="auditoria-bonificacion-panel">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex items-center justify-between w-full text-left"
        data-testid="auditoria-toggle-btn"
      >
        <span className="flex items-center gap-2 font-heading font-semibold text-municipal">
          <History className="w-4 h-4" /> Auditoría del módulo (quién hizo qué)
        </span>
        <span className="text-xs text-gray-400">{open ? "Ocultar" : "Mostrar"}</span>
      </button>

      {open && (
        <div className="mt-4">
          <div className="flex justify-end mb-3">
            <Button
              size="sm"
              variant="outline"
              onClick={(e) => { e.stopPropagation(); bonificacionExportarAuditoria(); }}
              data-testid="auditoria-export-btn"
            >
              <Download className="w-3.5 h-3.5 mr-1.5" /> Exportar a CSV
            </Button>
          </div>
          {loading && (
            <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-municipal" /></div>
          )}
          {!loading && logs && logs.length === 0 && (
            <p className="text-sm text-gray-400" data-testid="auditoria-empty">Todavía no hay acciones registradas en este módulo.</p>
          )}
          {!loading && logs && logs.length > 0 && (
            <ul className="divide-y divide-gray-100 max-h-96 overflow-y-auto" data-testid="auditoria-list">
              {logs.map((log, idx) => {
                const meta = ACCION_LABELS[log.accion] || { label: log.accion, icon: History, color: "text-gray-500" };
                const Icon = meta.icon;
                const detalle = detalleTexto(log.accion, log.detalle);
                return (
                  <li key={idx} className="py-3 flex items-start gap-3" data-testid={`auditoria-row-${idx}`}>
                    <Icon className={`w-4 h-4 mt-0.5 shrink-0 ${meta.color}`} />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-gray-700">
                        <span className="font-medium text-municipal">{log.actor}</span> — {meta.label}
                        {detalle && <span className="text-gray-400"> · {detalle}</span>}
                      </p>
                      <p className="text-xs text-gray-400">{new Date(log.fecha).toLocaleString("es-ES")}</p>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
